import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  TableBody,
  Typography,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Rating,
  TableContainer,
} from "@mui/material";
import { formatCurrency } from '../utils/currencyUtils';
import { applySalesMultiplier } from '../utils/chartUtils';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { BarChart } from "@mui/x-charts/BarChart";
import { formatChartValue, formatAxisValue } from '../utils/formatUtils';
import { colors } from '../utils/themeUtils';
import { NoMaxWidthTooltip, TooltipContent, CommentText } from './common/SharedTooltip';

// Helper function to capitalize the first letter of a string
const capitalizeFirstLetter = (string) => {
  if (!string || typeof string !== "string") return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Define colors for different sentiments
const sentimentColors = {
  positive: colors.positiveGreen,  // Green for positive sentiments
  negative: colors.negativeRed,    // Red for negative sentiments
  default: colors.textPrimary, // Default text color
};

// Custom hook to measure container width and determine which columns to display
const useResponsiveColumns = (containerRef) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [columns, setColumns] = useState({
    showReviewer: true,
    showDate: true,
    showRatingStars: true
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    // Initial measurement
    updateWidth();

    // Setup ResizeObserver
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);

    // Cleanup
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  // Determine which columns to show based on actual available width
  useEffect(() => {
    const newColumnState = {
      showReviewer: containerWidth > 350,
      // Always show date column regardless of width
      showDate: true,
      // Always show rating stars, even on mobile
      showRatingStars: true
    };
    setColumns(newColumnState);
  }, [containerWidth]);

  return columns;
};

const ReviewsTable = ({
  reviewsData = [],
  currencySymbol = "$",
  view = "Rating",
  selectedBusiness,
  showColoredText = false,
  showDateColumn = true,
  showBusinessName = false,
  businessName = "Dummy Business",
  businessAddress = "Dummy Address",
}) => {
  const tableContainerRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isLandscape = useMediaQuery('(orientation: landscape) and (max-width: 900px)');
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const isPortrait = useMediaQuery('(orientation: portrait)');

  // Sort reviews by date in descending order (newest first)
  const sortedReviews = useMemo(() => {
    return [...reviewsData].sort((a, b) => {
      return new Date(b.estimated_datetime) - new Date(a.estimated_datetime);
    });
  }, [reviewsData]);

  // Use custom hook to determine which columns to display
  const { showReviewer, showDate, showRatingStars } = useResponsiveColumns(tableContainerRef);

  // Format impact values with proper precision (for Rating view)
  const formatImpactExact = (impact) => {
    if (impact === undefined || impact === null) return '0.000 ★';
    
    const absImpact = Math.abs(impact);
    
    // For zero values
    if (absImpact === 0) return '0.000 ★';
    
    // For normal values (≥ 0.001), use standard 3 decimal places
    if (absImpact >= 0.001) {
      return `${absImpact.toFixed(3)} ★`;
    }
    
    // For very small values, find the first significant digit
    const scientificNotation = absImpact.toExponential();
    const matches = scientificNotation.match(/^(\d)\.?(\d*)?e-(\d+)$/);
    
    if (matches) {
      const firstDigit = matches[1]; // First significant digit
      const exponent = parseInt(matches[3], 10);
      
      // Create the string with leading zeros
      let result = '0.';
      for (let i = 1; i < exponent; i++) {
        result += '0';
      }
      result += firstDigit;
      
      return `${result} ★`;
    }
    
    // Fallback to standard formatting
    return `${absImpact.toFixed(3)} ★`;
  };

  // 5. Helper function to highlight review text based on sentiment and the two rules
  const highlightReviewText = (text, analysis, currencySymbol, view, turnover) => {
    if (!text) return "";

    // 1. Group all analysis items by their comment text
    const groupedAnalysis = analysis.reduce((acc, item) => {
      (acc[item.comment] = acc[item.comment] || []).push(item);
      return acc;
    }, {});

    // 2. Sort distinct comments by their position in the review text
    const sortedComments = Object.keys(groupedAnalysis).sort(
      (a, b) => text.indexOf(a) - text.indexOf(b)
    );

    const elements = [];
    let lastIndex = 0;

    // Format impact value function - both revenue and impact values with proper precision
    const formatImpactValue = (item) => {
      // Handle zero values with no sign
      if (item.attribute_impact === 0) {
        return view === "Sales" ? formatCurrency(0, currencySymbol, view) : '0.000 ★';
      }
      
      // For Sales view, calculate revenue impact using turnover
      if (view === "Sales") {
        const numericValue = applySalesMultiplier([item.attribute_impact], view, turnover)[0];
        const formattedValue = formatCurrency(numericValue, currencySymbol, view) || 'N/A';
        if (formattedValue === 'N/A') return 'N/A';
        const prefixedValue = numericValue > 0 ? `+${formattedValue}` : formattedValue;
        return prefixedValue;
      } else {
        // For Rating view, use our precision formatter
        const value = item.attribute_impact;
        const isPositive = value > 0;
        return `${isPositive ? '+' : '-'}${formatImpactExact(Math.abs(value))}`;
      }
    };

    // 3. Loop over each unique comment and build the text
    sortedComments.forEach((comment, index) => {
      const commentIndex = text.indexOf(comment, lastIndex);
      if (commentIndex === -1) return;

      // Add plain text before this comment
      if (commentIndex > lastIndex) {
        const plainText = text.substring(lastIndex, commentIndex);
        elements.push(plainText);
      }

      // All analysis items for this comment
      const items = groupedAnalysis[comment];

      // Determine the sentiment from the first item if we are coloring
      const sentiment = showColoredText ? (items[0].sentiment.toLowerCase() || 'default') : 'default';

      // If we should color & have a tooltip, wrap with tooltip. Otherwise, plain text.
      if (showColoredText) {
        elements.push(
          <NoMaxWidthTooltip 
            key={index} 
            title={<TooltipContent items={items} formatImpactValue={formatImpactValue} options={{view, currencySymbol}} />} 
            arrow 
            placement="top"
          >
            <CommentText sentiment={sentiment === 'positive' ? 'positive' : 'negative'}>
              {comment}
            </CommentText>
          </NoMaxWidthTooltip>
        );
      } else {
        // Render as plain text (no color, no tooltip)
        elements.push(comment);
      }

      // Update the lastIndex
      lastIndex = commentIndex + comment.length;
    });

    // Add remaining text after the last comment
    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      elements.push(remainingText);
    }

    return elements;
  };

  return (
    <Box sx={{
      alignItems: 'baseline',
      position: 'relative'
    }}>
      <Card sx={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
        borderRadius: { xs: '12px', sm: '16px', md: '24px' },
        border: '1px solid rgba(25, 118, 210, 0.1)',
        boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: '0 24px 48px -12px rgba(0,0,0,0.15)'
        },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #1976d2, #42a5f5)'
        }
      }}>
        <CardContent sx={{ 
          p: { 
            xs: isLandscape ? 0.5 : 1, 
            sm: 1.5, 
            md: 2 
          },
          '&:last-child': { 
            pb: { 
              xs: isLandscape ? 0.5 : 1, 
              sm: 1.5, 
              md: 2 
            } 
          }
        }}>
          {showBusinessName && (
            <Box sx={{
              mb: { 
                xs: isLandscape ? 0.5 : 1, 
                sm: 1.5, 
                md: 2 
              },
              display: 'flex',
              flexDirection: 'column',
              gap: isLandscape ? 0.25 : 0.5
            }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: '#0A2540',
                  fontSize: { 
                    xs: isLandscape ? '0.9rem' : '1rem', 
                    sm: '1.1rem', 
                    md: '1.25rem' 
                  }
                }}
              >
                {businessName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#546E7A',
                  fontSize: { 
                    xs: isLandscape ? '0.75rem' : '0.8rem', 
                    sm: '0.85rem', 
                    md: '0.9rem' 
                  }
                }}
              >
                {businessAddress}
              </Typography>
            </Box>
          )}

          <TableContainer 
            ref={tableContainerRef}
            sx={{ 
              mt: { 
                xs: isLandscape ? 0 : 0.5, 
                sm: 0.5, 
                md: 1 
              }
            }}
          >
            <Table 
              aria-label="reviews table"
              sx={{ 
                tableLayout: 'fixed',
                '& th, & td': {
                  px: { xs: 1, sm: 2 },
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {showReviewer && (
                    <TableCell sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                      width: { xs: '80px', sm: '120px', md: '150px' },
                      whiteSpace: 'normal',
                      overflow: 'hidden'
                    }}>Reviewer</TableCell>
                  )}
                  
                  {showDate && (
                    <TableCell sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                      width: { xs: '70px', sm: '110px', md: '120px' },
                      minWidth: { xs: '70px', sm: '110px', md: '120px' },
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      overflow: 'hidden',
                      textOverflow: { xs: 'clip', sm: 'ellipsis' },
                      lineHeight: { xs: 1.2, sm: 'normal' }
                    }}>Date</TableCell>
                  )}
                  
                  {showRatingStars && (
                    <TableCell align="center" sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                      width: { xs: '65px', sm: '120px' },
                      padding: { xs: '8px 6px', sm: '16px' }
                    }}>Rating</TableCell>
                  )}
                  
                  {/* Review column is always visible and takes remaining space */}
                  <TableCell sx={{
                    fontWeight: 700,
                    color: '#0A2540',
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                    width: 'auto'
                  }}>
                    <Typography sx={{
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}>
                      Review
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              
              <TableBody>
                {sortedReviews.length > 0 ? (
                  sortedReviews.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.04)',
                          transform: 'translateY(-2px)'
                        },
                        '& td': {
                          borderBottom: '1px solid rgba(25, 118, 210, 0.08)',
                          py: { xs: 1.5, sm: 2 }
                        }
                      }}
                    >
                      {showReviewer && (
                        <TableCell sx={{
                          color: '#0A2540',
                          fontWeight: 500,
                          fontSize: { xs: '0.875rem', md: '1rem' },
                          whiteSpace: 'normal',
                          overflow: 'hidden'
                        }}>{row.profile_name}</TableCell>
                      )}
                      
                      {showDate && (
                        <TableCell sx={{
                          color: '#546E7A',
                          fontSize: { xs: '0.875rem', md: '0.95rem' },
                          width: { xs: '70px', sm: '110px', md: '120px' },
                          minWidth: { xs: '70px', sm: '110px', md: '120px' },
                          whiteSpace: { xs: 'normal', sm: 'nowrap' },
                          overflow: 'hidden',
                          textOverflow: { xs: 'clip', sm: 'ellipsis' },
                          lineHeight: { xs: 1.2, sm: 'normal' }
                        }}>
                          {isSmallScreen && !isLandscape ? (
                            <>
                              {new Date(row.estimated_datetime).toLocaleDateString("en-GB").split('/').slice(0, 2).join('/')}/
                              <br />
                              {new Date(row.estimated_datetime).toLocaleDateString("en-GB").split('/')[2]}
                            </>
                          ) : (
                            new Date(row.estimated_datetime).toLocaleDateString("en-GB")
                          )}
                        </TableCell>
                      )}
                      
                      {showRatingStars && (
                        <TableCell align="center" sx={{
                          width: { xs: '65px', sm: '120px' },
                          padding: { xs: '8px 6px', sm: '16px' },
                          overflow: 'visible'
                        }}>
                          {isSmallScreen ? (
                            row.rating !== undefined && (
                              <Typography sx={{
                                color: '#0A2540',
                                fontWeight: 600,
                                fontSize: { xs: '0.875rem', md: '1rem' }
                              }}>
                                {row.rating}
                              </Typography>
                            )
                          ) : (
                            <Rating
                              value={row.rating}
                              readOnly
                              size="small"
                              sx={{
                                color: '#1976d2',
                                '& .MuiRating-iconEmpty': {
                                  color: 'rgba(25, 118, 210, 0.3)'
                                }
                              }}
                            />
                          )}
                        </TableCell>
                      )}
                      
                      {/* Review cell is always visible */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#0A2540',
                            fontSize: { xs: '0.875rem', md: '1rem' },
                            lineHeight: { 
                              xs: 1.4,
                              sm: 1.5,
                              md: 1.6 
                            }
                          }}
                        >
                          {highlightReviewText(
                            row.review_text,
                            row.analysis,
                            currencySymbol,
                            view,
                            selectedBusiness?.turnover
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      sx={{
                        textAlign: 'center',
                        py: 4,
                        color: '#546E7A',
                        fontSize: { xs: '0.875rem', md: '1rem' }
                      }}
                    >
                      No reviews found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReviewsTable;
